import { Component, OnInit, OnDestroy, ChangeDetectionStrategy, ChangeDetectorRef } from '@angular/core';
import { CommonModule } from '@angular/common';
import { RouterOutlet, RouterLink, RouterLinkActive } from '@angular/router';
import { BottomNavComponent } from './shared/bottom-nav.component';
import { Store } from '@ngrx/store';
import * as AuthActions from './store/auth/auth.actions';
import {
  selectIsAuthenticated,
  selectCurrentUser,
  selectUserRoles,
} from './store/auth/auth.selectors';
import { AuthService } from './shared/services/auth.service';
import { AssistantChatComponent } from './assistant/components/chat/assistant-chat.component';
import { appVersion } from '../environments/version';

@Component({
  changeDetection: ChangeDetectionStrategy.OnPush,
  selector: 'app-root',
  standalone: true,
  imports: [CommonModule, RouterOutlet, RouterLink, RouterLinkActive, BottomNavComponent, AssistantChatComponent],
  template: `
    <ng-container *ngIf="(currentUser$ | async) as user; else unauthShell">

      <!-- ── Top Bar ───────────────────────────────────────────── -->
      <nav class="navbar navbar-g8r px-3 topbar">
        <!-- Brand: shield logo + app name -->
        <a class="navbar-brand d-flex align-items-center gap-2" routerLink="/dashboard">
          <svg width="26" height="30" viewBox="0 0 38 44" fill="none" aria-hidden="true">
            <path d="M19 2L4 8.5V21C4 30.5 10.5 39 19 42C27.5 39 34 30.5 34 21V8.5L19 2Z"
                  fill="#162B55" stroke="#D4A017" stroke-width="1.5"/>
            <path d="M19 8L10 12.25V19.5C10 25.25 13.75 30.5 19 32.5C24.25 30.5 28 25.25 28 19.5V12.25L19 8Z"
                  fill="#0A1628" stroke="#D4A017" stroke-width="1"/>
            <text x="19" y="24" text-anchor="middle" font-family="monospace"
                  font-size="10" font-weight="500" fill="#D4A017" letter-spacing="-0.5">G8R</text>
          </svg>
          <span class="brand-wordmark">G8R Case</span>
        </a>

        <!-- Firm name badge and App Version -->
        <span class="firm-name" *ngIf="user.tenantName">
          {{ user.tenantName }}
        </span>
        <span class="app-version-badge ms-2" title="App Version">
          {{ appVersion }}
        </span>

        <!-- Topbar action icons -->
        <div class="topbar-actions ms-auto d-flex align-items-center gap-1">
          <button class="topbar-icon-btn" aria-label="Search" title="Search">
            <i class="bi bi-search"></i>
          </button>
          <button class="topbar-icon-btn" aria-label="Notifications" title="Notifications">
            <i class="bi bi-bell"></i>
          </button>

          <!-- ✦ Magic Wand — Assistant trigger ✦ -->
          <button
            class="topbar-icon-btn topbar-wand-btn"
            [class.wand-active]="assistantOpen"
            (click)="toggleAssistant()"
            aria-label="Open Assistant"
            title="Assistant">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.75"
                 width="18" height="18" aria-hidden="true">
              <!-- Wand shaft -->
              <line x1="4" y1="20" x2="14" y2="10" stroke-linecap="round" stroke-width="2"/>
              <!-- 4-point star at tip -->
              <path d="M17.5 2l.9 2.6L21 5.5l-2.6.9L17.5 9l-.9-2.6L14 5.5l2.6-.9z"
                    stroke-linejoin="round"/>
              <!-- Sparkle dots -->
              <circle cx="21.5" cy="2.5" r="1" fill="currentColor" stroke="none"/>
              <circle cx="22"   cy="8.5" r="0.75" fill="currentColor" stroke="none"/>
              <circle cx="14.5" cy="2"   r="0.75" fill="currentColor" stroke="none"/>
            </svg>
          </button>

          <a routerLink="/profile" class="g8r-avatar topbar-avatar"
             [attr.aria-label]="'Profile — ' + user.firstName">
            {{ (user.firstName[0] || '') + (user.lastName[0] || '') | uppercase }}
          </a>
        </div>
      </nav>

      <!-- ── App Body: sidebar + main + assistant panel ─────────── -->
      <div class="app-body">

        <!-- Labeled sidebar (160px) -->
        <aside class="g8r-sidebar sidebar-fixed"
               [class.sidebar-panel-hidden]="assistantOpen"
               aria-label="Primary navigation">

          <!-- Main group -->
          <p class="g8r-sidebar-label">Main</p>
          <nav class="g8r-nav-group">
            <a class="nav-link g8r-nav-link" routerLink="/dashboard" routerLinkActive="active"
               aria-label="Dashboard">
              <i class="bi bi-house"></i><span>Dashboard</span>
            </a>
            <a class="nav-link g8r-nav-link" routerLink="/crm/clients" routerLinkActive="active"
               aria-label="Contacts">
              <i class="bi bi-people"></i><span>Contacts</span>
            </a>
            <a class="nav-link g8r-nav-link" routerLink="/crm/matters" routerLinkActive="active"
               aria-label="Matters">
              <i class="bi bi-briefcase"></i><span>Matters</span>
            </a>
            <a class="nav-link g8r-nav-link" routerLink="/crm/conflicts" routerLinkActive="active"
               aria-label="Conflicts">
              <i class="bi bi-shield-exclamation"></i><span>Conflicts</span>
            </a>
            <a class="nav-link g8r-nav-link" routerLink="/crm/tasks" routerLinkActive="active"
               aria-label="Tasks">
              <i class="bi bi-check2-square"></i><span>Tasks</span>
            </a>
            <a class="nav-link g8r-nav-link" routerLink="/calendar" routerLinkActive="active"
               aria-label="Calendar">
              <i class="bi bi-calendar3"></i><span>Calendar</span>
            </a>
            <a class="nav-link g8r-nav-link" routerLink="/docket" routerLinkActive="active"
               aria-label="Docket">
              <i class="bi bi-journal-text"></i><span>Docket</span>
            </a>
          </nav>

          <!-- Billing group -->
          <p class="g8r-sidebar-label">Billing</p>
          <nav class="g8r-nav-group">
            <a class="nav-link g8r-nav-link" routerLink="/billing/invoices" routerLinkActive="active"
               aria-label="Invoices">
              <i class="bi bi-receipt"></i><span>Invoices</span>
            </a>
            <a class="nav-link g8r-nav-link" routerLink="/billing/time" routerLinkActive="active"
               aria-label="Time">
              <i class="bi bi-clock"></i><span>Time</span>
            </a>
            <a class="nav-link g8r-nav-link" routerLink="/reports" routerLinkActive="active"
               aria-label="Reports">
              <i class="bi bi-bar-chart"></i><span>Reports</span>
            </a>
          </nav>

          <!-- Tools group -->
          <p class="g8r-sidebar-label">Tools</p>
          <nav class="g8r-nav-group">
            <a class="nav-link g8r-nav-link" routerLink="/assistant" routerLinkActive="active"
               aria-label="Assistant">
              <i class="bi bi-stars"></i><span>Assistant</span>
            </a>
          </nav>

          <!-- Admin (conditional) -->
          <ng-container *ngIf="(userRoles$ | async)?.includes('admin')">
            <p class="g8r-sidebar-label">Admin</p>
            <nav class="g8r-nav-group">
              <a class="nav-link g8r-nav-link" routerLink="/admin" routerLinkActive="active"
                 [routerLinkActiveOptions]="{ exact: true }" aria-label="Admin">
                <i class="bi bi-gear"></i><span>Admin</span>
              </a>
              <a class="nav-link g8r-nav-link" routerLink="/admin/ai-telemetry" routerLinkActive="active"
                 aria-label="AI Telemetry">
                <i class="bi bi-activity"></i><span>AI Telemetry</span>
              </a>
            </nav>
          </ng-container>

          <!-- Sidebar footer: logout -->
          <div class="sidebar-footer">
            <div class="sidebar-nav-sep"></div>
            <button class="nav-link g8r-nav-link g8r-nav-btn w-100"
                    (click)="logout()" aria-label="Logout">
              <i class="bi bi-box-arrow-left"></i>
              <span>Logout</span>
            </button>
          </div>

        </aside>

        <!-- Main content (pushes left when assistant opens) -->
        <main class="app-main" [class.panel-open]="assistantOpen" [class.panel-expanded]="assistantExpanded" role="main">
          <router-outlet></router-outlet>
        </main>

        <!-- ✦ Assistant slide-out panel (right edge) ✦ -->
        <div class="mobile-assistant-backdrop"
             *ngIf="assistantOpen && isMobile"
             (click)="toggleAssistant()"
             aria-hidden="true">
        </div>
        <aside class="assistant-panel" [class.open]="assistantOpen"
               [class.expanded]="assistantExpanded"
               aria-label="Assistant" [attr.aria-hidden]="!assistantOpen">
          <!-- Panel header -->
          <div class="asst-panel-header">
            <div class="asst-panel-icon" aria-hidden="true">
              <i class="bi bi-stars"></i>
            </div>
            <span class="asst-panel-title">Assistant</span>
            <button class="asst-panel-expand"
                    (click)="toggleAssistantExpand()"
                    [attr.aria-label]="assistantExpanded ? 'Collapse to panel' : 'Expand to full workspace'"
                    [title]="assistantExpanded ? 'Collapse to panel' : 'Expand to full workspace'">
              <i [class]="assistantExpanded ? 'bi bi-arrows-angle-contract' : 'bi bi-arrows-angle-expand'"></i>
            </button>
            <button class="asst-panel-close" (click)="toggleAssistant()" aria-label="Close assistant">
              <i class="bi bi-x-lg"></i>
            </button>
          </div>
          <!-- Embedded assistant component in panel mode -->
          <app-assistant-chat *ngIf="assistantOpen" [panelMode]="true" [expandedMode]="assistantExpanded"></app-assistant-chat>
        </aside>

      </div>

      <!-- Mobile bottom nav -->
      <app-bottom-nav [show]="isMobile" (moreClicked)="toggleMoreMenu()"></app-bottom-nav>

      <!-- ✦ Mobile More Sheet ✦ -->
      <ng-container *ngIf="isMobile && showMobileMore">
        <div class="mobile-more-backdrop" (click)="toggleMoreMenu()" aria-hidden="true"></div>
        <nav class="mobile-more-sheet" aria-label="More navigation">
          <div class="mobile-more-handle" aria-hidden="true"></div>
          <div class="mobile-more-user-row">
            <div class="g8r-avatar mobile-more-avatar">
              {{ (user.firstName[0] || '') + (user.lastName[0] || '') | uppercase }}
            </div>
            <div class="mobile-more-user-info">
              <div class="mobile-more-user-name">{{ user.firstName }} {{ user.lastName }}</div>
              <a routerLink="/profile" class="mobile-more-profile-link" (click)="toggleMoreMenu()">View Profile</a>
            </div>
          </div>
          <div class="mobile-more-sep"></div>
          <p class="mobile-more-section-lbl">Work</p>
          <a class="mobile-more-item" routerLink="/crm/tasks" routerLinkActive="active" (click)="toggleMoreMenu()">
            <i class="bi bi-check2-square"></i><span>Tasks</span>
          </a>
          <a class="mobile-more-item" routerLink="/crm/conflicts" routerLinkActive="active" (click)="toggleMoreMenu()">
            <i class="bi bi-shield-exclamation"></i><span>Conflicts</span>
          </a>
          <a class="mobile-more-item" routerLink="/calendar" routerLinkActive="active" (click)="toggleMoreMenu()">
            <i class="bi bi-calendar3"></i><span>Calendar</span>
          </a>
          <p class="mobile-more-section-lbl">Billing</p>
          <a class="mobile-more-item" routerLink="/billing/invoices" routerLinkActive="active" (click)="toggleMoreMenu()">
            <i class="bi bi-receipt"></i><span>Invoices</span>
          </a>
          <a class="mobile-more-item" routerLink="/billing/time" routerLinkActive="active" (click)="toggleMoreMenu()">
            <i class="bi bi-clock"></i><span>Time</span>
          </a>
          <a class="mobile-more-item" routerLink="/reports" routerLinkActive="active" (click)="toggleMoreMenu()">
            <i class="bi bi-bar-chart"></i><span>Reports</span>
          </a>
          <ng-container *ngIf="(userRoles$ | async)?.includes('admin')">
            <p class="mobile-more-section-lbl">Admin</p>
            <a class="mobile-more-item" routerLink="/admin" routerLinkActive="active"
               [routerLinkActiveOptions]="{ exact: true }" (click)="toggleMoreMenu()">
              <i class="bi bi-gear"></i><span>Admin</span>
            </a>
            <a class="mobile-more-item" routerLink="/admin/ai-telemetry" routerLinkActive="active" (click)="toggleMoreMenu()">
              <i class="bi bi-activity"></i><span>AI Telemetry</span>
            </a>
          </ng-container>
          <div class="mobile-more-sep"></div>
          <button class="mobile-more-item mobile-more-logout" (click)="logout(); toggleMoreMenu()">
            <i class="bi bi-box-arrow-left"></i><span>Log out</span>
          </button>
          <div style="height: 4px;"></div>
        </nav>
      </ng-container>

    </ng-container>

    <!-- ── Unauthenticated shell (login page, no sidebar) ─────── -->
    <ng-template #unauthShell>
      <router-outlet></router-outlet>
    </ng-template>
  `,
  styles: [
    `
      /* ── Topbar ─────────────────────────────────────────────── */
      .topbar {
        position: fixed;
        top: 0;
        left: 0;
        right: 0;
        z-index: 1030;
        height: var(--g8r-topbar-height);
        min-height: var(--g8r-topbar-height);
        display: flex;
        align-items: center;
        padding-top: 0 !important;
        padding-bottom: 0 !important;
        flex-wrap: nowrap;
      }

      .brand-wordmark {
        font-family: var(--g8r-font-display);
        font-size: 1.15rem;
        font-weight: 700;
        color: #fff;
        letter-spacing: -0.025em;
      }

      .firm-name {
        font-family: var(--g8r-font-mono);
        font-size: 0.7rem;
        font-weight: 500;
        letter-spacing: 0.1em;
        text-transform: uppercase;
        color: var(--g8r-navy-300);
        padding: 0.2rem 0.65rem;
        border: 1px solid var(--g8r-navy-700);
        border-radius: 9999px;
        margin-left: 0.75rem;
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
        max-width: 200px;
      }

      .app-version-badge {
        font-family: var(--g8r-font-mono);
        font-size: 0.65rem;
        font-weight: 600;
        color: var(--g8r-gold-500);
        background: rgba(212, 160, 23, 0.1);
        padding: 0.2rem 0.5rem;
        border-radius: 6px;
        border: 1px solid rgba(212, 160, 23, 0.2);
        white-space: nowrap;
      }

      .topbar-icon-btn {
        background: none;
        border: none;
        color: var(--g8r-navy-200);
        font-size: 1.1rem;
        width: 32px;
        height: 32px;
        border-radius: var(--g8r-radius);
        cursor: pointer;
        display: flex;
        align-items: center;
        justify-content: center;
        transition: color 0.15s, background 0.15s;
        padding: 0;
        position: relative;
      }
      .topbar-icon-btn:hover {
        color: #fff;
        background: rgba(255, 255, 255, 0.08);
      }

      /* Magic wand — active state */
      .topbar-wand-btn.wand-active {
        color: var(--g8r-gold-400);
        background: rgba(212, 160, 23, 0.15);
      }
      .topbar-wand-btn.wand-active:hover {
        color: var(--g8r-gold-300);
        background: rgba(212, 160, 23, 0.22);
      }
      .topbar-wand-btn.wand-active::after {
        content: '';
        position: absolute;
        inset: 4px;
        border-radius: calc(var(--g8r-radius) - 2px);
        box-shadow: 0 0 0 2px rgba(212, 160, 23, 0.35);
        pointer-events: none;
      }

      .topbar-avatar {
        width: 30px;
        height: 30px;
        border-radius: 50%;
        background: var(--g8r-navy-600);
        border: 2px solid var(--g8r-gold-500);
        display: inline-flex;
        align-items: center;
        justify-content: center;
        font-family: var(--g8r-font-body);
        font-size: 0.7rem;
        font-weight: 700;
        color: #fff;
        text-decoration: none;
        flex-shrink: 0;
        margin-left: 0.25rem;
        cursor: pointer;
        transition: border-color 0.15s;
      }
      .topbar-avatar:hover {
        border-color: var(--g8r-gold-300);
        color: #fff;
      }

      /* ── App Body Layout ─────────────────────────────────────── */
      .app-body {
        display: flex;
        min-height: 100vh;
        padding-top: var(--g8r-topbar-height);
        overflow: hidden;
      }

      /* ── Labeled Sidebar (160px) ────────────────────────────── */
      .sidebar-fixed {
        position: fixed;
        top: var(--g8r-topbar-height);
        left: 0;
        bottom: 0;
        width: var(--g8r-sidebar-width);
        background: var(--g8r-navy-900);
        border-right: 1px solid var(--g8r-navy-800);
        overflow-y: auto;
        overflow-x: visible;
        z-index: 1020;
        display: flex;
        flex-direction: column;
        padding-top: 0.5rem;
        padding-bottom: 0.5rem;
        scrollbar-width: none;
      }
      .sidebar-fixed::-webkit-scrollbar { display: none; }

      /* Sidebar section labels */
      .g8r-sidebar-label {
        font-family: var(--g8r-font-mono);
        font-size: 0.6rem;
        font-weight: 600;
        letter-spacing: 0.1em;
        text-transform: uppercase;
        color: var(--g8r-navy-500);
        padding: 0.6rem 1.1rem 0.2rem;
        margin: 0;
      }

      /* Labeled sidebar nav */
      .g8r-nav-group {
        padding: 0;
      }

      .g8r-nav-link {
        display: flex;
        align-items: center;
        gap: 0.55rem;
        padding: 0.375rem 1rem 0.375rem 1.1rem;
        font-size: 0.8125rem;
        font-weight: 500;
        color: var(--g8r-navy-300);
        border-left: 2px solid transparent;
        border-radius: 0;
        text-decoration: none;
        white-space: nowrap;
        transition: background 0.12s, border-color 0.12s, color 0.12s;
        cursor: pointer;
        background: none;
        border-top: none;
        border-right: none;
        border-bottom: none;
        width: 100%;
        text-align: left;
      }
      .g8r-nav-link i {
        font-size: 0.85rem;
        flex-shrink: 0;
        opacity: 0.85;
      }
      .g8r-nav-link:hover {
        color: #fff;
        background: rgba(255, 255, 255, 0.07);
        border-left-color: rgba(212, 160, 23, 0.5);
        text-decoration: none;
      }
      .g8r-nav-link.active {
        color: #fff;
        background: rgba(255, 255, 255, 0.11);
        border-left-color: var(--g8r-gold-500);
        font-weight: 600;
      }

      .g8r-nav-btn {
        background: none;
        border: none;
      }

      .sidebar-nav-sep {
        height: 1px;
        background: rgba(255, 255, 255, 0.06);
        margin: 0.25rem 1rem;
      }

      .sidebar-footer {
        margin-top: auto;
        border-top: 1px solid rgba(255,255,255,0.06);
        padding-top: 0.35rem;
      }

      /* ── Main Content ──────────────────────────────────────────  */
      .app-main {
        flex: 1;
        margin-left: var(--g8r-sidebar-width);
        margin-right: 0;
        padding: 0;
        min-height: calc(100vh - var(--g8r-topbar-height));
        background: var(--g8r-bg-light);
        overflow-y: auto;
        transition: margin-right 0.28s cubic-bezier(0.4,0,0.2,1);
      }
      .app-main.panel-open {
        margin-right: var(--g8r-assistant-panel-width);
      }

      /* ── Assistant Slide-Out Panel ───────────────────────────── */
      .assistant-panel {
        position: fixed;
        top: var(--g8r-topbar-height);
        right: 0;
        bottom: 0;
        width: 0;
        background: #fff;
        border-left: 1px solid transparent;
        display: flex;
        flex-direction: column;
        overflow: hidden;
        z-index: 1010;
        transition: width 0.28s cubic-bezier(0.4,0,0.2,1),
                    border-color 0.28s;
      }
      .assistant-panel.open {
        width: var(--g8r-assistant-panel-width);
        border-left-color: var(--g8r-border);
        box-shadow: -4px 0 20px rgba(10,22,40,0.08);
      }

      /* Assistant chat fills remaining panel height and scrolls internally */
      .assistant-panel app-assistant-chat {
        flex: 1;
        min-height: 0;
        overflow: hidden;
        display: flex;
        flex-direction: column;
      }

      /* Panel header */
      .asst-panel-header {
        height: 54px;
        background: var(--g8r-navy-900);
        display: flex;
        align-items: center;
        padding: 0 14px;
        gap: 10px;
        flex-shrink: 0;
        border-bottom: 1px solid var(--g8r-navy-800);
      }

      .asst-panel-icon {
        width: 28px;
        height: 28px;
        background: linear-gradient(135deg, var(--g8r-navy-600), var(--g8r-navy-400));
        border-radius: 7px;
        display: flex;
        align-items: center;
        justify-content: center;
        flex-shrink: 0;
        color: #fff;
        font-size: 0.85rem;
      }

      .asst-panel-title {
        font-family: var(--g8r-font-display);
        font-size: 0.9rem;
        font-weight: 700;
        color: #fff;
        letter-spacing: -0.01em;
        flex: 1;
        white-space: nowrap;
      }

      .asst-panel-close {
        background: rgba(255,255,255,0.10);
        border: none;
        color: var(--g8r-navy-200);
        border-radius: 6px;
        width: 28px;
        height: 28px;
        display: flex;
        align-items: center;
        justify-content: center;
        cursor: pointer;
        flex-shrink: 0;
        font-size: 0.8rem;
        transition: background 0.1s, color 0.1s;
      }
      .asst-panel-close:hover {
        background: rgba(255,255,255,0.18);
        color: #fff;
      }

      /* ── Expanded panel fills all workspace right of sidebar ─── */
      .assistant-panel.open.expanded {
        width: calc(100vw - var(--g8r-sidebar-width));
        box-shadow: none;
        border-left-color: transparent;
      }

      /* Main content fades out when panel is expanded */
      .app-main.panel-open.panel-expanded {
        opacity: 0;
        pointer-events: none;
      }

      /* Expand button — same visual style as close button */
      .asst-panel-expand {
        background: rgba(255,255,255,0.10);
        border: none;
        color: var(--g8r-navy-200);
        border-radius: 6px;
        width: 28px;
        height: 28px;
        display: flex;
        align-items: center;
        justify-content: center;
        cursor: pointer;
        flex-shrink: 0;
        font-size: 0.75rem;
        transition: background 0.1s, color 0.1s;
      }

      .asst-panel-expand:hover {
        background: rgba(255,255,255,0.18);
        color: #fff;
      }

      /* Mobile backdrop — hidden by default, shown in media query below */
      .mobile-assistant-backdrop {
        display: none;
      }

      /* ── Mobile More Sheet ────────────────────────────────────── */
      .mobile-more-backdrop {
        position: fixed;
        inset: 0;
        background: rgba(5, 12, 26, 0.45);
        z-index: 1040;
        animation: moreBackdropIn 0.18s ease;
      }
      @keyframes moreBackdropIn {
        from { opacity: 0; }
        to   { opacity: 1; }
      }
      .mobile-more-sheet {
        position: fixed;
        bottom: 56px;
        left: 0;
        right: 0;
        background: #fff;
        border-top: 1px solid var(--g8r-border);
        border-radius: 16px 16px 0 0;
        z-index: 1045;
        max-height: calc(100vh - 96px);
        overflow-y: auto;
        animation: moreSheetUp 0.22s ease;
        scrollbar-width: none;
      }
      .mobile-more-sheet::-webkit-scrollbar { display: none; }
      @keyframes moreSheetUp {
        from { transform: translateY(100%); }
        to   { transform: translateY(0); }
      }
      .mobile-more-handle {
        width: 36px;
        height: 3px;
        background: var(--g8r-navy-300);
        border-radius: 2px;
        margin: 10px auto 4px;
      }
      .mobile-more-user-row {
        display: flex;
        align-items: center;
        gap: 0.75rem;
        padding: 10px 16px 12px;
      }
      .mobile-more-avatar {
        width: 38px !important;
        height: 38px !important;
        font-size: 0.8rem !important;
        flex-shrink: 0;
      }
      .mobile-more-user-info {
        display: flex;
        flex-direction: column;
        gap: 2px;
      }
      .mobile-more-user-name {
        font-size: 0.875rem;
        font-weight: 600;
        color: var(--g8r-navy-900);
        line-height: 1.3;
      }
      .mobile-more-profile-link {
        font-size: 0.72rem;
        color: var(--g8r-navy-600);
        text-decoration: none;
      }
      .mobile-more-profile-link:hover { color: var(--g8r-navy-800); }
      .mobile-more-sep {
        height: 1px;
        background: var(--g8r-border);
        margin: 4px 0;
      }
      .mobile-more-section-lbl {
        font-family: var(--g8r-font-mono);
        font-size: 0.6rem;
        font-weight: 600;
        letter-spacing: 0.1em;
        text-transform: uppercase;
        color: var(--g8r-navy-400);
        padding: 8px 16px 2px;
        margin: 0;
      }
      .mobile-more-item {
        display: flex;
        align-items: center;
        gap: 0.875rem;
        padding: 11px 16px;
        font-size: 0.875rem;
        font-weight: 500;
        color: var(--g8r-navy-800);
        text-decoration: none;
        cursor: pointer;
        background: none;
        border: none;
        width: 100%;
        text-align: left;
        -webkit-tap-highlight-color: transparent;
        transition: background 0.1s;
      }
      .mobile-more-item i {
        font-size: 1rem;
        color: var(--g8r-navy-400);
        width: 18px;
        text-align: center;
        flex-shrink: 0;
      }
      .mobile-more-item:active { background: var(--g8r-navy-50); }
      .mobile-more-item.active { color: var(--g8r-navy-900); }
      .mobile-more-item.active i { color: var(--g8r-gold-600); }
      .mobile-more-logout { color: #c0392b; }
      .mobile-more-logout i { color: #c0392b; }

      /* ── Responsive ──────────────────────────────────────────── */
      @media (max-width: 1023px) {
        .firm-name { max-width: 140px; }
      }

      @media (max-width: 767px) {
        .firm-name { display: none; }

        /* Sidebar hidden on mobile — bottom nav handles navigation */
        .sidebar-fixed {
          display: none !important;
        }

        .app-main {
          margin-left: 0;
          padding: 0;
          padding-bottom: calc(56px + 1rem);
        }
        /* On mobile, panel is full-width overlay, not push */
        .app-main.panel-open {
          margin-right: 0;
        }
        .assistant-panel.open {
          width: 100vw;
          box-shadow: none;
        }

        /* Hide sidebar when assistant panel is open — prevents 48px left clip */
        .sidebar-fixed.sidebar-panel-hidden {
          display: none;
        }

        /* Raise panel above sidebar */
        .assistant-panel {
          z-index: 1025;
        }

        /* Backdrop: semi-transparent overlay covering content behind panel */
        .mobile-assistant-backdrop {
          display: block;
          position: fixed;
          inset: 0;
          background: rgba(5, 12, 26, 0.45);
          z-index: 1015;
          animation: fadeIn 0.2s ease;
        }

        @keyframes fadeIn {
          from { opacity: 0; }
          to   { opacity: 1; }
        }

        /* Hide version badge — irrelevant to end users on mobile */
        .app-version-badge {
          display: none;
        }

        /* Hide expand button — panel is already full-screen on mobile */
        .asst-panel-expand {
          display: none;
        }

        /* Compact panel header */
        .asst-panel-header {
          height: 48px;
          padding: 0 12px;
        }

        .asst-panel-title {
          font-size: 0.82rem;
        }
      }
    `,
  ],
})
export class AppComponent implements OnInit, OnDestroy {
  title = 'G8R Case';
  appVersion = appVersion;
  isAuthenticated$ = this.store.select(selectIsAuthenticated);
  currentUser$ = this.store.select(selectCurrentUser);
  userRoles$ = this.store.select(selectUserRoles);
  isMobile = false;
  assistantOpen = false;
  assistantExpanded = false;
  showMobileMore = false;
  private resizeListener: (() => void) | undefined;

  constructor(
    private store: Store,
    private authService: AuthService,
    private cdr: ChangeDetectorRef,
  ) {
    this.hydrateSessionFromStorage();
  }

  ngOnInit(): void {
    this.checkViewport();
    this.resizeListener = () => {
      this.checkViewport();
      this.cdr.markForCheck();
    };
    window.addEventListener('resize', this.resizeListener);
  }

  ngOnDestroy(): void {
    if (this.resizeListener) {
      window.removeEventListener('resize', this.resizeListener);
    }
  }

  checkViewport(): void {
    this.isMobile = window.innerWidth <= 767;
  }

  toggleAssistant(): void {
    this.assistantOpen = !this.assistantOpen;
    if (!this.assistantOpen) {
      this.assistantExpanded = false;
    }
    this.cdr.markForCheck();
  }

  toggleAssistantExpand(): void {
    this.assistantExpanded = !this.assistantExpanded;
    this.cdr.markForCheck();
  }

  toggleMoreMenu(): void {
    this.showMobileMore = !this.showMobileMore;
    this.cdr.markForCheck();
  }

  logout(): void {
    this.store.dispatch(AuthActions.logout());
  }

  private hydrateSessionFromStorage(): void {
    this.authService.initSession().subscribe((session) => {
      if (session) {
        this.store.dispatch(
          AuthActions.hydrateSession({
            user: session.user,
            accessToken: session.accessToken,
          }),
        );
      }
    });
  }
}
